/**
 * searchActions.js
 * Created by michaelbray on 2/13/18.
 */

import axios from 'axios';
import { find } from 'lodash';

import { urlWithParams } from '../../helpers/urlHelper';
import * as ActionTypes from '../actionTypes';
import * as SecurityHelper from './../../helpers/securityHelper';
import * as FocusManager from '../../components/sharedComponents/FocusManager/FocusManager';
import SessionStorage from '../../helpers/storage/SessionStorage';

const inventoryTypeFilters = [
    {
        key: 'camping',
        value: 'Camping/Lodging',
        filters: [
            {
                attribute: 'campsite_type_of_use',
                filterValue: 'Overnight,na',
            },
            {
                attribute: 'entity_type',
                filterValue: 'campground',
            },
        ],
    },
    {
        key: 'dayuse',
        value: 'Day Use',
        filters: [
            {
                attribute: 'campsite_type_of_use',
                filterValue: 'Day',
            },
            {
                attribute: 'entity_type',
                filterValue: 'campground',
            },
        ],
    },
    {
        key: 'tours',
        value: 'Tours',
        filters: [
            {
                attribute: 'entity_type',
                filterValue: 'ticketfacility,timedentry',
            },
            {
                attribute: 'campsite_type_of_use',
                filterValue: 'na',
            },
        ],
    },
    {
        key: 'tickettours',
        value: 'Tickets',
        filters: [
            {
                attribute: 'entity_type',
                filterValue: 'ticketfacility,timedentry,tour,timedentry_tour',
            },
            {
                attribute: 'campsite_type_of_use',
                filterValue: 'na',
            },
        ],
    },
    {
        key: 'permits',
        value: 'Permits',
        filters: [
            {
                attribute: 'entity_type',
                filterValue: 'permit,vehiclepermit',
            },
            {
                attribute: 'campsite_type_of_use',
                filterValue: 'na',
            },
        ],
    },
    {
        key: 'pass',
        value: 'Pass',
        filters: [
            {
                attribute: 'entity_type',
                filterValue: 'pass,activitypass'
            },
            {
                attribute: 'campsite_type_of_use',
                filterValue: 'na'
            }
        ]
    },
    {
        key: 'activitypass',
        value: 'Activity Pass',
        filters: [
            {
                attribute: 'entity_type',
                filterValue: 'activitypass'
            },
            {
                attribute: 'campsite_type_of_use',
                filterValue: 'na'
            }
        ]
    },
    {
        key: 'treepermit',
        value: 'Tree Permits',
        filters: [
            {
                attribute: 'entity_type',
                filterValue: 'treepermit'
            },
            {
                attribute: 'campsite_type_of_use',
                filterValue: 'na'
            }
        ]
    },
    {
        key: 'poi',
        value: 'Points of Interest',
        filters: [
            {
                attribute: 'reservable',
                filterValue: '0',
            },
            {
                attribute: 'campsite_type_of_use',
                filterValue: 'na',
            },
        ],
    },
    {
        key: 'recarea',
        value: 'Rec Area',
        filters: [
            {
                attribute: 'entity_type',
                filterValue: 'recarea'
            },
            {
                attribute: 'campsite_type_of_use',
                filterValue: 'na',
            },
        ],
    },
];

// update search criteria state with a new value for a name parameter
export function updateSearchCriteria(name, value) {
    return function (dispatch) {
        return dispatch({
            type: ActionTypes.UPDATE_SEARCH_CRITERIA,
            name,
            value,
        });
    };
}

// update search criteria state with a new set of values
export function updateSearchCriterias(params) {
    return function (dispatch) {
        const st = Object.assign({}, params);
        st.type = ActionTypes.UPDATE_SEARCH_CRITERIAS;
        return dispatch(st);
    };
}

// update search criteria by removing a filter
export function removeSearchFilter(uiFilterName, uiFilterOption) {
    return {
        type: ActionTypes.REMOVE_SEARCH_FILTER,
        uiFilterName,
        uiFilterOption,
    };
}

// update search criteria by adding a filter
export function addSearchFilter(uiFilterName, uiFilterOption) {
    return {
        type: ActionTypes.ADD_SEARCH_FILTER,
        uiFilterName,
        uiFilterOption,
    };
}

// update search criteria by setting a filter value
export function setSearchFilter(uiFilterName, uiFilterOption) {
    return {
        type: ActionTypes.SET_SEARCH_FILTER,
        uiFilterName,
        uiFilterOption,
    };
}

// reset search filters (other than what/where/lat/lng)
export function resetSearchFilters() {
    return {
        type: ActionTypes.RESET_SEARCH_FILTERS,
    };
}

// Fetch suggestions, using search service API
export function fetchSuggestions(prefix) {
    return (dispatch) => {
        return axios
            .get(`${process.env.API}/search/suggest`, {
                params: {
                    q: prefix,
                    geocoder: true,
                },
                headers: { Authorization: SecurityHelper.getAuthHeader() },
            })
            .then((success) => {
                dispatch({
                    type: ActionTypes.GET_SEARCH_SUGGESTIONS,
                    suggestions: success.data.suggestions,
                    inventory_suggestions: success.data.inventory_suggestions,
                    content_suggestions: success.data.content_suggestions,
                });
            })
            .catch((error) => {
                dispatch({
                    type: ActionTypes.GET_SEARCH_SUGGESTIONS_ERROR,
                    err: `${error}`,
                });
            });
    };
}

// helper method to construct search GET uri (the uri is the same for search service call as well as for front end)
export function buildSearchNavUri(baseUri, props) {
    // create limited set of search parameters that can be encoded into uri - the rest are persisted in sessionStore
    const params = {
        q: props.search.what,
        entity_id: props.search.entity_id,
        entity_type: props.search.entity_type,
        lat: props.search.lat,
        lng: props.search.lng,
        lat_sw: props.search.lat_sw,
        lng_sw: props.search.lng_sw,
        lat_ne: props.search.lat_ne,
        lng_ne: props.search.lng_ne,
        radius: props.search.radius,
        checkin: props.search.checkin_time,
        checkout: props.search.checkout_time,
        inventory_type: props.search.inventory_type,
        parent_asset_id: props.search.parent_asset_id,
        sort: props.search.sort,
    };
    return urlWithParams(baseUri, params);
}

// show search results in response to a user action using current search state
export function navigateToSearchResults() {
    return function (dispatch, getState) {
        const state = getState();
        const uri = buildSearchNavUri('/search', state);
        window.location.assign(uri);
    };
}

// add inventory type filter to search criteria
export function setInventoryTypeFilters(types) {
    return function (dispatch) {
        // add optional inventory filters
        if (types) {
            const inventoryTypes = types;
            inventoryTypes.forEach((name) => {
                const inventoryTypeFilter = find(inventoryTypeFilters, { key: name });

                if (inventoryTypeFilter) {
                    dispatch(addSearchFilter('inventoryTypes', inventoryTypeFilter));
                }
            });
        }
    };
}

// track search request in GA and execute search
export function trackRunSearch(gaEvent, searchCallback) {
    try {
        // try to publish GA event to measure suggestions vs search
        if (window.dataLayer) {
            let pushCompleted = false;
            const params = { ...gaEvent };
            params.event = 'eventTracking';
            let fallbackTimeout = null;
            params.eventCallback = () => {
                pushCompleted = true;
                searchCallback();
                if (fallbackTimeout) {
                    clearTimeout(fallbackTimeout);
                }
            };
            params.eventTimeout = 1000;
            // fallback in case push fails
            fallbackTimeout = setTimeout(() => {
                if (!pushCompleted) {
                    searchCallback();
                }
            }, 1500);
            window.dataLayer.push(params);
        } else {
            searchCallback();
        }
    } catch (ex) {
        searchCallback();
    }
}

// show search results for a given simple criteria - query, and optional inventory types, resetting search state and clearing any other filters
export function submitSimpleSearch(
    optionalQuery,
    optionalInventoryTypes,
    optionalSuggestion,
    optionalParentAssetId
) {
    return (dispatch) => {
        // reset existing filters/search state
        dispatch(resetSearchFilters());

        // add optional inventory type filter
        dispatch(setInventoryTypeFilters(optionalInventoryTypes));

        FocusManager.clearManagedPaths();

        let lat;
        let lng;
        let radius;
        let location;
        if (
            optionalSuggestion &&
            !optionalSuggestion.entity_type &&
            optionalSuggestion.lat &&
            optionalSuggestion.lng
        ) {
            // default search to suggestion location
            lat = optionalSuggestion.lat;
            lng = optionalSuggestion.lng;
            radius = 200;
            location = optionalSuggestion.text;
        }

        const criteria = {
            what: optionalQuery,
            headerTextQuery: optionalQuery,
            entity_id: optionalSuggestion != null ? optionalSuggestion.entity_id : null,
            entity_type:
                optionalSuggestion != null ? optionalSuggestion.entity_type : null,
            inventory_type: optionalInventoryTypes,
            parent_asset_id: optionalParentAssetId,
            highlighted_item: -1,
            exact: false,
            lat,
            lng,
            radius,
            map_center_lat: lat,
            map_center_lng: lng,
            start: null,
            cursor_mark: null,
            location,
            placename: null,
            lat_sw: null,
            lng_sw: null,
            lat_ne: null,
            lng_ne: null,
        };

        if (
            optionalInventoryTypes &&
            (!optionalQuery || optionalQuery === '') &&
            optionalInventoryTypes.length === 1 &&
            (optionalInventoryTypes[0] === 'tours' ||
                optionalInventoryTypes[0] === 'timedentry_tours' ||
                optionalInventoryTypes[0] === 'permits')
        ) {
            // for tours only, set bounding box for search to entire US and sort results by
            // distance/proximity to user's location
            criteria.lng_sw = -167.2815442995964;
            criteria.lat_sw = 3.6722362889127;
            criteria.lng_ne = -51.637969429829155;
            criteria.lat_ne = 60.77726184625004;
            criteria.sort = 'distance';
        }

        dispatch(updateSearchCriterias(criteria));
        dispatch(navigateToSearchResults());
    };
}

// fetch and return user geoip data, could be from session store cache
export async function fetchUserGeoIp() {
    const resultString = SessionStorage.getItem('geoip');

    if (resultString) {
        // returned cached result
        const geopipCache = JSON.parse(resultString);
        return new Promise((resolve) => {
            resolve(geopipCache);
        });
    }

    // geocode user's ip
    return new Promise((resolve) => {
        axios
            .get(`${process.env.API}/recommendation/geoip`, {
                params: {},
            })
            .then((result) => {
                const ret = result.data;
                SessionStorage.setItem('geoip', JSON.stringify(ret));
                resolve(ret);
            })
            .catch((error) => {
                console.log('error fetching geoip', error);
                resolve({});
            });
    });
}

// Fetch inventory locations
export async function fetchNearbyInventoryLocations(
    lat = null,
    lng = null,
    inventoryTypes = [],
    size = 20,
    isAcrossUS = false,
    sort = "score",
    additionalFilters = [],
    start = 0,
    aggregations = [],
    locationName = '',
) {
    const filters = [];
    inventoryTypes.forEach((name) => {
        const inventoryTypeFilter = find(inventoryTypeFilters, { key: name });
        inventoryTypeFilter.filters.forEach((filter) => {
            const parts = filter.filterValue.split(',');
            parts.forEach((value) => {
                filters.push(`${filter.attribute}:${value}`);
            });
        });
    });
    filters.push(...additionalFilters);
    const params = {
        fq: filters,
        sort,
        start,
        size,
        agg: aggregations,
    };
    if (lat && lng) {
        params.lat = lat;
        params.lng = lng;
        params.location = locationName;
    }
    if (isAcrossUS) {
        // search within specific area
        params.lng_sw = -167.2815442995964;
        params.lat_sw = 3.6722362889127;
        params.lng_ne = -51.637969429829155;
        params.lat_ne = 60.77726184625004;
    } else {
        // search within radius from lat/lng
        params.radius = 300;
    }
    return axios.get(urlWithParams(`${process.env.API}/search`, params));
}
